import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";
import {
  foldAccents,
  setFieldValue,
} from "../../shared/field_values";
import {
  setSubmitBlock,
  setSubmitPrompt,
  submitButtonsFor,
} from "../../shared/submit_blocks";

// Whether anything else on this form has an opinion
// about why the CTA is held. Matched on the controller
// attribute rather than asked of Stimulus: this
// controller has no handle on its peers, and the
// question is only ever "is one mounted here".
const QUOTE_CONTROLLER =
  '[data-controller~="courier-quote"]';

// Blank, whitespace and absent all mean "nothing to
// print here". Shared by every line this controller
// writes, so a field that is only spaces cannot end up
// as a stray comma in one summary and nothing in
// another.
const present = (value) =>
  value && String(value).trim().length > 0;

// How long either Places proxy call gets before the
// browser calls it dead.
//
// A `fetch` whose connection goes away does not reject;
// the promise simply never settles, and this checkout is
// ordered from phones on mobile data. The details call is
// the one that strands somebody: a pick that never
// answers leaves the customer looking at a search bar
// holding an address, over a form nothing filled in, with
// neither the address fields nor the confirmation on
// screen and no failure to explain either (CAL-464).
//
// The number is set from the SERVER's own ceiling rather
// than from what feels tolerable, because killing a
// request the server is still working on manufactures a
// failure and wastes the Google call it interrupts. Each
// endpoint makes ONE Places call, capped at
// `GooglePlaces::REQUEST_TIMEOUT` (10s, a global budget
// covering connect, write and read together), and nothing
// else shortens it: there is no rack-timeout in the app
// and no proxy cap in front of it. So ten seconds of
// Google plus ten for Rails' own work and the round trip
// to a phone, the same headroom the quote endpoint's
// deadline carries over its own ceiling.
const REQUEST_TIMEOUT_MS = 20 * 1000;

// The parts of an address the page states back to the
// customer: the row, the sheet's confirmation card, the
// route map's legend. What the server drew them from
// arrives as `drawnAddress`, with these same keys
// (`QuoteComponent::DRAWN_ADDRESS_FIELDS`).
const STATED_KEYS = [
  "street_address",
  "interior",
  "suburb",
  "postal_code",
  "state",
  "city"
];

/**
 * Address Autocomplete Controller
 *
 * Single-field address picker for delivery / shipping
 * checkout. Wraps the Google Places autocomplete
 * proxy + place details proxy so the user can type
 * once, pick a suggestion, and have the form
 * populated automatically.
 *
 * TWO PAGES MOUNT IT, and since CAL-453 what a pick
 * leaves on screen differs between them:
 *
 *   - The catalog shipping checkout is one long form.
 *     A pick fills it, unhides the contact fields and
 *     opens the address detail fields, the only place
 *     that page restates what Google answered.
 *   - The restaurant delivery checkout puts the search
 *     in a `<dialog>` sheet that also holds the
 *     interior, the access notes and the same detail
 *     fields. A pick keeps that sheet OPEN and reveals
 *     them under a card restating the address, so the
 *     detail fields stay folded there
 *     (`expandDetailsOnPick`).
 *
 * Either way the search bar stays, so a wrong pick is
 * corrected by picking again, and the detail fields are
 * one toggle away rather than one page away.
 *
 * The server owns the starting state (a saved address
 * or a 422 redisplay renders those surfaces without
 * `hidden`), and `connect` reads that back rather than
 * assuming a fresh checkout.
 *
 * Every sheet target is optional and every read of one
 * is guarded: the shipping checkout renders none.
 */
export default class extends Controller {
  static targets = [
    "searchInput",
    "results",
    "addressSummary",
    "summaryLine",
    "contactFields",
    "addressDetails",
    "fields",
    "manualToggle",
    "manualLabelExpand",
    "manualLabelCollapse",
    "editDetailsLabelExpand",
    "editDetailsLabelCollapse",
    // The compact two-line form of the same summary,
    // used by the checkout's address context row: the
    // street on top, where it is underneath. All four
    // are optional: the shipping checkout renders the
    // single `summaryLine` and none of these, so every
    // write below is guarded.
    "summaryPrimary",
    "summarySecondary",
    // The two states of that row. The empty one is a
    // faint prompt with a chevron, the filled one a
    // two-line address with an Edit action; they are
    // different enough to be two elements rather than
    // one with six conditional classes.
    "summaryEmpty",
    "summaryFilled",
    // The delivery sheet's own post-pick surface: the
    // group holding the interior, the notes and the
    // details disclosure, and inside it the card that
    // restates the address. Two targets rather than
    // one because different things reveal them: the
    // manual link opens the group with nothing to
    // confirm inside it. Optional like the four above:
    // the shipping checkout renders none of them.
    "sheetSummary",
    "sheetSummaryCard",
    "sheetSummaryPrimary",
    "sheetSummarySecondary"
  ];
  static values = {
    autocompleteUrl: String,
    detailsUrl: String,
    // Whether a pick also opens the address detail
    // fields. An opt-OUT: the shipping checkout has no
    // confirmation card, so those fields ARE its
    // confirmation of the pick and it keeps the
    // behavior it has always had. See `applyAddress`.
    expandDetailsOnPick: {type: Boolean, default: true},
    // The address the server drew this page for, key for
    // key (`STATED_KEYS`): the one the row, the sheet and
    // the route map card state. Blank for a guest. See
    // `restateUndrawnAddress`.
    drawnAddress: {type: Object, default: {}}
  };

  connect() {
    this.sessionToken = this.generateSessionToken();
    // Guarded because `connect` runs twice against one
    // `disconnect` on a Turbo morph (CAL-401): Turbo
    // reconnects Stimulus natively AND
    // `entrypoints/users.js` walks live controllers
    // calling `disconnect(); connect()`. Re-running the
    // bare assignment would debounce the ALREADY
    // debounced function, adding 250ms to the wait on
    // every morph.
    if (!this.searchDebounced) {
      this.searchDebounced = debounce(
        this.search.bind(this),
        250
      );
      this.search = this.searchDebounced;
    }
    this.boundOutsideClick =
      this.onOutsideClick.bind(this);
    document.addEventListener(
      "click",
      this.boundOutsideClick
    );
    // The server already decided whether the
    // address summary / contact / details wrappers
    // start hidden (saved address, validation-error
    // redisplay → no `hidden` class). Mirror that
    // decision on the toggle links so they don't sit
    // next to surfaces that are already visible.
    if (this.addressSummaryVisible()) {
      this.hideManualToggle();
    }
    this.syncEditDetailsLabel();
    this.syncManualLabel();
    this.restateUndrawnAddress();
  }

  /**
   * An address the fields hold and the page was not
   * drawn for, which is what a morph leaves behind after
   * a render by a server that never saw it.
   *
   * A Google pick is purely client-side, so the server
   * only ever draws an address the customer SAVED, or
   * none. The customer morph keeps what they typed and
   * picked (`shared/customer_morph.js`), but everything
   * this page derives from the address comes back as the
   * server drew it. For a guest that is the row reading
   * "Enter your delivery address", the sheet's post-pick
   * group folded, the route map card hidden and the
   * Place order button held on `addressBlocked`. For a
   * signed-in customer it is WORSE, because it looks
   * fine: the row, the map's legend and its centre all
   * name the SAVED address, over fields, a pin and a
   * quote that are the one they picked. Measured on
   * CAL-522, both, by applying a discount code: the
   * signed-in page showed "Calle Orizaba 101" and would
   * have sent the courier to Tamaulipas.
   *
   * THE FIELDS ARE THE TRUTH; what the page states
   * follows them. So the rule is never "restate over an
   * empty row" (that is the guest's case only, and it is
   * how the signed-in one shipped): it is "the fields
   * hold an address other than the one the server drew",
   * compared key by key with `drawnAddress`, which the
   * server writes from the same values it drew the row
   * from. Then the address is stated again, from the
   * fields. The row, the sheet and the button's address
   * hold are ours and are set here and now. Everything
   * else listens for the event a pick dispatches, so the
   * restatement dispatches the same one (the route map,
   * the radius check, the pin, the state and city
   * lists) carrying the pin the customer placed, which
   * the morph kept too. `courier-quote` asks for a new
   * price on its own reconnect: the code changed the
   * basket the fee is priced on.
   *
   * On a first paint the fields and the row come from
   * one address, so this is a no-op there, and the same
   * for a signed-in customer who changed nothing, whose
   * saved address stands. Choosing another SAVED address
   * is a round trip the server draws, so it agrees too.
   * Only the delivery checkout renders the row; the
   * shipping checkout has no `summaryFilled` target and
   * is left alone.
   */
  restateUndrawnAddress() {
    if (!this.hasSummaryFilledTarget) return;

    const address = this.addressFromFields();
    if (this.drawnFor(address)) return;

    this.renderSummary(address);
    if (!present(address.street_address)) return;

    this.showSheetSummary();
    this.announceAfterRender();
  }

  /**
   * Whether the server drew this page for the address
   * the fields hold.
   *
   * A text field is compared as it stands. A `<select>`
   * is compared the way `city-selector` resolves a city
   * onto its list, accents and case folded, since the
   * list's "Cuauhtemoc" IS the saved "Cuauhtémoc"; and a
   * select holding nothing is a list mid-reload, not a
   * different address. The server cannot mark a city
   * `CS.cities` does not carry ("Ciudad de México"), so
   * on a first paint that select reads blank until
   * `city-selector` adds it: an incomplete read of the
   * address must not be taken for a new one (CAL-464).
   */
  drawnFor(address) {
    const drawn = this.drawnAddressValue || {};
    const fields = new Map(
      this.fieldsTargets.map((field) => [
        this.fieldName(field),
        field
      ])
    );

    return STATED_KEYS.every((key) => {
      const field = fields.get(key);
      if (!field) return true;

      const held = String(address[key] ?? "").trim();
      const said = String(drawn[key] ?? "").trim();
      if (field.tagName !== "SELECT") return held === said;

      return held === "" || foldAccents(held) === foldAccents(said);
    });
  }

  /**
   * NOT from inside the render. The radius check this
   * event sets off submits a Turbo form, and a submit
   * fired from a reconnect lands in the middle of the
   * visit that reconnected it: `turbo:load` never fires
   * and the progress bar spins forever over a finished
   * page (CAL-401, the reason `address-selector` latches
   * its own connect). `turbo:load` is the visit
   * finishing.
   *
   * Once per render: a replaced element connects twice
   * against one `turbo:load`, and the second connect
   * replaces the announcement the first one scheduled
   * rather than adding another. The fields are read
   * when it fires, not now, so an edit in between is
   * what gets announced, and nothing is announced into
   * a page this controller has left.
   */
  announceAfterRender() {
    if (this.pendingAnnouncement) {
      document.removeEventListener(
        "turbo:load",
        this.pendingAnnouncement
      );
    }

    this.pendingAnnouncement = () => {
      this.pendingAnnouncement = null;
      if (!this.element.isConnected) return;

      const address = this.addressFromFields();
      if (!present(address.street_address)) return;

      window.dispatchEvent(
        new CustomEvent(
          "address-selector:addressSelected",
          {
            detail: {
              address,
              location: this.placedPin()
            }
          }
        )
      );
    };

    document.addEventListener(
      "turbo:load",
      this.pendingAnnouncement,
      { once: true }
    );
  }

  /**
   * The drop-off pin the order form is holding: the one
   * a pick placed or the customer dragged. It lives in
   * the route map card, a sibling of this element, so it
   * is read through the form. Both coordinates or
   * neither, the rule every reader of a pin keeps.
   */
  placedPin() {
    const form = this.element.closest("form");
    if (!form) return null;

    const coordinate = (axis) =>
      form
        .querySelector(`[name$="_pin_${axis}]"]`)
        ?.value.trim() || "";
    const latitude = coordinate("latitude");
    const longitude = coordinate("longitude");
    if (latitude === "" || longitude === "") return null;

    return { latitude, longitude };
  }

  disconnect() {
    document.removeEventListener(
      "click",
      this.boundOutsideClick
    );
  }

  generateSessionToken() {
    if (
      typeof crypto !== "undefined" &&
      crypto.randomUUID
    ) {
      return crypto.randomUUID();
    }
    return (
      Math.random().toString(36).slice(2) +
      Date.now().toString(36)
    );
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults();
    }
  }

  addressSummaryVisible() {
    return (
      this.hasAddressSummaryTarget &&
      !this.addressSummaryTarget.classList.contains(
        "hidden"
      )
    );
  }

  addressDetailsVisible() {
    return (
      this.hasAddressDetailsTarget &&
      !this.addressDetailsTarget.classList.contains(
        "hidden"
      )
    );
  }

  /**
   * Blur is the last resort for autofill (CAL-407).
   * Chrome fires `input`/`change` when it fills a
   * field, but not every browser does: some leave the
   * value sitting there silently until focus moves on,
   * and the customer is then looking at a filled
   * address bar and a quote panel still asking for an
   * address.
   *
   * Two guards, and both are load-bearing:
   *
   * - **Results already open.** Blur fires BEFORE the
   *   click on a suggestion lands, so searching here
   *   would re-render the list out from under the
   *   cursor and the pick would miss. If the list is
   *   up, the customer is mid-choice; leave them
   *   alone.
   * - **The value is the one already picked.** After
   *   `select` the input holds the chosen address, and
   *   re-searching it would pop the suggestions back
   *   over a form that is already filled in. Compared
   *   by TEXT rather than a "has picked" flag on
   *   purpose: autofill can overwrite the field after
   *   a pick, and that is a new address which still
   *   deserves the search.
   *
   * What is left is exactly the autofill case: a value
   * the user never typed, no list ever shown, nothing
   * matching it picked.
   */
  searchOnBlur() {
    const value = this.searchInputTarget.value.trim();
    if (value.length < 2) return;
    if (this.resultsVisible()) return;
    if (value === this.selectedDisplayText) return;

    this.search();
  }

  resultsVisible() {
    return (
      this.hasResultsTarget &&
      !this.resultsTarget.classList.contains("hidden")
    );
  }

  async search() {
    const input = this.searchInputTarget.value.trim();
    if (input.length < 2) {
      this.hideResults();
      return;
    }
    const url =
      `${this.autocompleteUrlValue}?` +
      `input=${encodeURIComponent(input)}` +
      `&session_token=${this.sessionToken}`;
    const deadline = this.startDeadline();
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: deadline.signal
      });
      if (!response.ok) return;
      const data = await response.json();
      this.renderResults(data.suggestions || []);
    } catch (e) {
      // Swallow, our own deadline's abort included. A
      // search that answers nothing changes nothing on
      // screen, which is what every other failure of this
      // fetch already does: the suggestion list is a
      // convenience the customer can do without, and
      // "Enter address manually instead" is on the page
      // throughout. A timeout gets no copy of its own.
    } finally {
      deadline.clear();
    }
  }

  /**
   * A deadline for one customer-facing `fetch`.
   *
   * One controller per attempt, handed back for the
   * caller to keep in a local rather than parked on
   * `this`: the timer can then only ever abort the
   * request it was made for, so a deadline left over
   * from a keystroke the customer has already superseded
   * cannot reach through and kill the one in flight now.
   * Every caller clears it in a `finally`.
   */
  startDeadline() {
    const abort = new AbortController();
    const timer = window.setTimeout(
      () => abort.abort(),
      REQUEST_TIMEOUT_MS
    );

    return {
      signal: abort.signal,
      clear: () => window.clearTimeout(timer)
    };
  }

  renderResults(suggestions) {
    this.resultsTarget.innerHTML = "";
    if (suggestions.length === 0) {
      this.hideResults();
      return;
    }
    suggestions.forEach((s) => {
      const placePrediction = s.placePrediction;
      if (!placePrediction) return;
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "text-left w-full p-2 hover:bg-base-200 " +
        "rounded";
      // Real Google responses always include
      // `structuredFormat`. Some test stubs and
      // upstream variations only set `text.text`, so
      // fall back to that to avoid blank rows.
      const main =
        placePrediction.structuredFormat?.mainText
          ?.text ||
        placePrediction.text?.text ||
        "";
      const secondary =
        placePrediction.structuredFormat?.secondaryText
          ?.text || "";
      const mainEl = document.createElement("div");
      mainEl.className = "font-medium";
      mainEl.textContent = main;
      const secondaryEl =
        document.createElement("div");
      secondaryEl.className =
        "text-sm text-base-content/60";
      secondaryEl.textContent = secondary;
      button.appendChild(mainEl);
      button.appendChild(secondaryEl);
      button.dataset.placeId =
        placePrediction.placeId;
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.select(placePrediction);
      });
      item.appendChild(button);
      this.resultsTarget.appendChild(item);
    });
    this.resultsTarget.classList.remove("hidden");
  }

  async select(placePrediction) {
    const displayText =
      placePrediction.text?.text ||
      placePrediction.structuredFormat?.mainText
        ?.text ||
      "";
    this.searchInputTarget.value = displayText;
    // Remembered so a later blur can tell "the address
    // the customer picked" from "something that
    // appeared in the field on its own" (CAL-407).
    this.selectedDisplayText = displayText.trim();
    this.hideResults();

    const deadline = this.startDeadline();
    try {
      const url =
        `${this.detailsUrlValue}/` +
        `${encodeURIComponent(
          placePrediction.placeId
        )}` +
        `?session_token=${this.sessionToken}`;
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: deadline.signal
      });
      if (!response.ok) {
        // Network / API problem: fall through to
        // manual entry rather than leaving the user
        // staring at a one-line search bar with no
        // way forward.
        this.fallBackToManualEntry();
        return;
      }
      const data = await response.json();
      this.applyAddress(data.address || {}, data.location);
    } catch (e) {
      // A connection that died on the way back lands
      // here now that it has a deadline, alongside every
      // other transport failure, and wants the same
      // answer they get: nothing was resolved, so open
      // the fields and let them type it. No new copy.
      this.fallBackToManualEntry();
    } finally {
      deadline.clear();
      // Pick rotates the session token: Google bills
      // the autocomplete + the details call as one
      // session, the next typing run is a new one.
      this.sessionToken = this.generateSessionToken();
    }
  }

  /**
   * "Enter address manually instead", and every path
   * where a pick produced no address to confirm.
   *
   * There is no second sheet to open any more: the
   * search sheet and the manual sheet both rendered
   * every address input, and the empty copy, whichever
   * one the customer had not filled in, is what posted
   * (CAL-453). So the manual link reveals the SAME
   * group a pick does, in place, with the fields empty
   * and the disclosure already open.
   *
   * The confirmation card deliberately stays down: an
   * accent box restating an address the customer never
   * gave is a lie in the shape of a reassurance. Only
   * `renderSummary` raises it, and only once there is a
   * street in it.
   */
  enterManually(event) {
    event?.preventDefault();

    this.fallBackToManualEntry();
  }

  fallBackToManualEntry() {
    this.showSheetSummary();
    this.showAddressDetails();
    this.showContactFields();
  }

  // Populate every form field whose name matches one
  // of the address keys, restate the address, and
  // reveal the surfaces that only make sense once
  // there is one.
  //
  // An empty payload applies nothing and falls back to
  // manual entry instead.
  //
  // `location` is the pin Google resolved for this
  // place: `{latitude, longitude}`, the shape the
  // Places API returns. It rides along in the event
  // so the pin fields and the radius validation use
  // it instead of geocoding the address text we just
  // assembled FROM it: two Google calls for a worse
  // answer (CAL-413).
  applyAddress(address, location = null) {
    if (!address || Object.keys(address).length === 0) {
      this.fallBackToManualEntry();
      return;
    }
    this.fieldsTargets.forEach((field) => {
      const name = this.fieldName(field);
      if (!name) return;
      const value = address[name];
      if (value === undefined || value === null) {
        // A pick is AUTHORITATIVE for the interior, so
        // an absent one clears the box rather than
        // leaving it alone.
        //
        // Every other key here can survive a stale
        // value harmlessly-ish, but this one is the
        // apartment a courier knocks on: pick a flat,
        // change your mind, pick a house, and the house
        // was quoted and dispatched with the flat's
        // number still attached (it rides in the quote
        // payload and the permitted params). It would
        // also disagree with the summary line, which is
        // drawn from the payload and would show no
        // interior while the form held one.
        if (name === "interior" && field.value !== "") {
          field.value = "";
          field.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }
        return;
      }
      // Through `setFieldValue` because two of these
      // fields are `<select>`s and one of them cannot
      // hold what a pick hands it. The city list belongs
      // to the state the SERVER rendered, so the moment
      // a customer corrects their address into another
      // state, every city in it is unknown to the
      // options on screen and a plain assignment empties
      // the field instead of filling it (CAL-464).
      setFieldValue(field, value);
      field.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    });

    // The state and city selectors listen for this
    // window-scoped event (from address-selector) to
    // load their options. Reusing it keeps the two
    // entry points (saved address vs autocomplete)
    // wired through the same downstream.
    window.dispatchEvent(
      new CustomEvent(
        "address-selector:addressSelected",
        { detail: { address, location } }
      )
    );

    this.renderSummary(address);
    this.showAddressSummary();
    this.showContactFields();
    // The pick keeps the delivery sheet OPEN and
    // reveals its post-pick group instead of closing
    // it. CAL-420 closed the sheet on every pick
    // because leaving it up hid the whole confirmation
    // of what the customer had just done behind a panel
    // whose only exit was an unlabelled ✕; the sheet
    // now confirms the address in a card of its own,
    // above the two things only the customer can supply
    // (CAL-453).
    this.showSheetSummary();
    // The detail fields were expanded here so a wrong
    // colonia from Google was visible before submit.
    // That check has not been dropped: the card now
    // carries it, and six inputs above the interior and
    // the access notes push both under the fold on a
    // phone. The shipping checkout has no card, so
    // those fields remain its only confirmation and it
    // keeps expanding them.
    if (this.expandDetailsOnPickValue) {
      this.showAddressDetails();
    }
  }

  /**
   * Restate the summary from whatever the address
   * fields hold RIGHT NOW, rather than from a payload.
   * Three paths change those fields (a Google pick, a
   * saved address, the manual form) and only the
   * first of them has a payload to hand, so reading
   * the DOM is what keeps all three honest.
   *
   * Wired as an action from the manual sheet's save
   * button and from `address-selector:addressSelected`.
   */
  refreshSummary() {
    this.renderSummary(this.addressFromFields());
  }

  addressFromFields() {
    const address = {};
    this.fieldsTargets.forEach((field) => {
      const name = this.fieldName(field);
      if (name) address[name] = field.value;
    });
    return address;
  }

  // Build a single-line summary like
  //   "Av. Tamaulipas 66, Hipódromo Condesa,
  //    Cuauhtemoc, CMX 06100"
  // and write it into the summary `<p>`. Country is
  // intentionally omitted (we're MX-only).
  //
  // The checkout's address row wants the same address
  // split over two lines instead, and wants the row to
  // stop being a prompt once there is one. Both are
  // guarded, because the shipping checkout renders
  // neither.
  renderSummary(address) {
    const stateAndPostal = [
      address.state,
      address.postal_code
    ]
      .filter(Boolean)
      .join(" ");
    // The street line as a person reads it. Mirrors
    // `Address#street_line` server-side: an apartment
    // number the customer supplied and the page never
    // echoes back looks exactly like one that was
    // dropped, and after CAL-442 it sometimes had been.
    const streetLine = present(address.interior)
      ? `${String(address.street_address).trim()}, ` +
        `int. ${String(address.interior).trim()}`
      : address.street_address;
    const parts = [
      streetLine,
      address.suburb,
      address.city,
      stateAndPostal
    ].filter(present);

    if (this.hasSummaryLineTarget) {
      this.summaryLineTarget.textContent =
        parts.join(", ");
    }
    if (this.hasSummaryPrimaryTarget) {
      this.summaryPrimaryTarget.textContent = present(
        address.street_address
      )
        ? String(streetLine).trim()
        : parts.join(", ");
    }
    if (this.hasSummarySecondaryTarget) {
      this.summarySecondaryTarget.textContent = [
        address.suburb,
        address.city
      ]
        .filter(present)
        .join(" · ");
    }

    this.renderSheetConfirmation(address);

    this.toggleSummaryState(
      present(address.street_address)
    );
  }

  /**
   * The delivery sheet's own confirmation of the
   * address, in the same shape the server renders into
   * the same two spans: the street, then where it is.
   *
   * Same address as the row behind the sheet, a
   * different sentence: the row is two lines of page
   * chrome, this is what the customer reads before they
   * close the sheet on it.
   *
   * The card follows the street rather than the group:
   * the group is opened by anything that needs the
   * fields, including "Enter address manually instead",
   * and an accent box confirming an address nobody has
   * given yet is worse than no box at all.
   */
  renderSheetConfirmation(address) {
    const street = present(address.street_address)
      ? String(address.street_address).trim()
      : "";

    if (this.hasSheetSummaryPrimaryTarget) {
      this.sheetSummaryPrimaryTarget.textContent =
        street;
    }
    if (this.hasSheetSummarySecondaryTarget) {
      this.sheetSummarySecondaryTarget.textContent = [
        address.suburb,
        address.city,
        address.state
      ]
        .filter(present)
        .join(", ");
    }
    if (this.hasSheetSummaryCardTarget) {
      this.sheetSummaryCardTarget.classList.toggle(
        "hidden",
        street === ""
      );
    }
  }

  toggleSummaryState(filled) {
    if (this.hasSummaryEmptyTarget) {
      this.summaryEmptyTarget.classList.toggle(
        "hidden",
        filled
      );
    }
    if (this.hasSummaryFilledTarget) {
      this.summaryFilledTarget.classList.toggle(
        "hidden",
        !filled
      );
    }

    this.releaseAddressBlock(filled);
  }

  /**
   * The delivery checkout ships its Place order button
   * disabled until it has an address, and on the
   * new-address path there is no round trip to lift
   * that: `address-autocomplete` writes the row and
   * the inputs client-side, and the server never sees
   * it. Without this a guest types their address,
   * closes the sheet, and finds the button dead with
   * no way back short of reloading.
   *
   * Only ever CLEARS, never sets: the server decides
   * whether to gate. That is what makes this safe in a
   * controller the catalog shipping checkout also
   * mounts: nothing there sets `addressBlocked`, so
   * nothing is cleared and this cannot introduce a
   * gate on a page that never asked for one.
   *
   * A courier delivery still holds on `quoteBlocked`
   * until its quote lands: `setSubmitBlock` recomputes
   * `disabled` as the OR of every reason, so releasing
   * this one alone does not open the button.
   */
  releaseAddressBlock(filled) {
    if (!filled) return;

    const form = this.element.closest("form");
    if (!form) return;

    submitButtonsFor(form).forEach((button) => {
      setSubmitBlock(button, "addressBlocked", false);
    });

    // The copy is only ours to clear where the address
    // was the LAST thing missing. On a courier delivery
    // the button stays held for the price, and turning
    // the reason off would put "Place order" over a
    // placeholder for as long as the quote takes: the
    // exact live-CTA-with-no-amount this gate exists to
    // prevent, rebuilt one layer up. `courier-quote`
    // owns the sentence from here and swaps it to the
    // pending one on the same keystroke.
    if (form.querySelector(QUOTE_CONTROLLER)) return;

    setSubmitPrompt(form, false);
  }

  // Extract a field name from the form field's
  // `name` attribute. Mirrors the regex used by
  // address-selector so the two controllers handle
  // the same set of name shapes:
  //   "...[address_attributes][street_address]"
  //   "...[delivery_street_address]"
  //   "...[shipping_street_address]"
  fieldName(field) {
    const nestedMatch = field.name.match(
      /\[address_attributes\]\[([^\]]+)\]/
    );
    if (nestedMatch) return nestedMatch[1];
    const flatMatch = field.name.match(
      /\[(?:delivery|shipping)_([^\]]+)\]/
    );
    if (flatMatch) return flatMatch[1];
    return null;
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden");
    }
  }

  showAddressSummary() {
    if (this.hasAddressSummaryTarget) {
      this.addressSummaryTarget.classList.remove(
        "hidden"
      );
    }
    this.hideManualToggle();
  }

  showContactFields() {
    if (this.hasContactFieldsTarget) {
      this.contactFieldsTarget.classList.remove(
        "hidden"
      );
    }
  }

  // Everything in the delivery sheet that only makes
  // sense once there is an address to talk about: the
  // confirmation card, the interior, the access notes
  // and the details disclosure. A Google pick is purely
  // client-side, so the server never hears about it and
  // cannot open this on its own (CAL-445).
  showSheetSummary() {
    if (this.hasSheetSummaryTarget) {
      this.sheetSummaryTarget.classList.remove("hidden");
    }
  }

  showAddressDetails() {
    if (this.hasAddressDetailsTarget) {
      this.addressDetailsTarget.classList.remove(
        "hidden"
      );
    }
    this.syncEditDetailsLabel();
  }

  hideAddressDetails() {
    if (this.hasAddressDetailsTarget) {
      this.addressDetailsTarget.classList.add(
        "hidden"
      );
    }
    this.syncEditDetailsLabel();
  }

  // Swap which label span is visible inside the
  // toggle link based on the details panel's current
  // state. "Edit address details" when collapsed,
  // "Hide address details" when expanded.
  syncEditDetailsLabel() {
    const expanded = this.addressDetailsVisible();
    if (this.hasEditDetailsLabelExpandTarget) {
      this.editDetailsLabelExpandTarget
        .classList.toggle("hidden", expanded);
    }
    if (this.hasEditDetailsLabelCollapseTarget) {
      this.editDetailsLabelCollapseTarget
        .classList.toggle("hidden", !expanded);
    }
  }

  hideManualToggle() {
    if (this.hasManualToggleTarget) {
      this.manualToggleTarget.classList.add("hidden");
    }
  }

  // "Or enter address manually" / "Hide address
  // fields": toggles the manual entry surface.
  // Expanding shows contact + detail fields and
  // hides the summary card (manual flow is
  // independent of any prior pick). Collapsing
  // hides them all back, leaving just the search
  // bar.
  toggleManual(event) {
    event.preventDefault();
    if (this.contactFieldsVisible()) {
      this.hideContactFields();
      this.hideAddressDetails();
    } else {
      this.showContactFields();
      this.showAddressDetails();
      if (this.hasAddressSummaryTarget) {
        this.addressSummaryTarget.classList.add(
          "hidden"
        );
      }
    }
    this.syncManualLabel();
  }

  contactFieldsVisible() {
    return (
      this.hasContactFieldsTarget &&
      !this.contactFieldsTarget.classList.contains(
        "hidden"
      )
    );
  }

  hideContactFields() {
    if (this.hasContactFieldsTarget) {
      this.contactFieldsTarget.classList.add(
        "hidden"
      );
    }
  }

  // Swap which manual-toggle label is visible. Same
  // pattern as `syncEditDetailsLabel`: "Or enter
  // address manually" when collapsed, "Hide address
  // fields" when the manual surface is expanded.
  syncManualLabel() {
    const expanded = this.contactFieldsVisible();
    if (this.hasManualLabelExpandTarget) {
      this.manualLabelExpandTarget
        .classList.toggle("hidden", expanded);
    }
    if (this.hasManualLabelCollapseTarget) {
      this.manualLabelCollapseTarget
        .classList.toggle("hidden", !expanded);
    }
  }

  // "Edit address details" / "Hide address details":
  // toggle the detail panel. Keeps the summary up
  // either way.
  toggleAddressDetails(event) {
    event.preventDefault();
    if (this.addressDetailsVisible()) {
      this.hideAddressDetails();
    } else {
      this.showAddressDetails();
    }
  }
}
